import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { cloneDeep, remove } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ScenarioDiagramContext } from '../../../state/Context';
import { TextField } from '../../form';
import { useScenarioPatientCategoryMapQuery } from '../../../network/queries';
import { useUpdateScenarioPatientCategoryMutation } from '../../../network/mutations';

function JourneyDialog() {
  const { t } = useTranslation();
  const { scenarioId } = useParams();
  const {
    state: {
      open,
      data: { unit },
      activePatientCategory,
    },
    setState,
  } = useContext(ScenarioDiagramContext);
  const {
    mutate,
    isPending: isMutationPending,
    isSuccess: isMutationSuccess,
  } = useUpdateScenarioPatientCategoryMutation();

  const { isLoading: isLoadingPatientCategoryMap, data: patientCategoryMap } =
    useScenarioPatientCategoryMapQuery(scenarioId);
  const [patientCategory, setPatientCategory] = useState({});
  const [journey, setJourney] = useState({ options: [] });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      items: [],
    },
  });
  const { fields, replace } = useFieldArray({ control, name: 'items' });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (
      open &&
      unit &&
      activePatientCategory &&
      !isLoadingPatientCategoryMap &&
      patientCategoryMap
    ) {
      const patientCategory = patientCategoryMap.find(
        (i) => i.id === activePatientCategory,
      );
      const journey = patientCategory.journey.find(
        (i) => i.start_unit === unit.name,
      );
      setPatientCategory(patientCategory);
      setJourney(journey);
    }
  }, [
    open,
    unit,
    activePatientCategory,
    isLoadingPatientCategoryMap,
    patientCategoryMap,
  ]);

  useEffect(() => {
    if (open && journey && journey.options) {
      replace(
        journey.options.map((option) => ({
          start_unit: journey.start_unit,
          end_unit: option.id,
          proportion: option.proportion,
        })),
      );
    }
  }, [open, journey]);

  const onClose = () => {
    setState((prev) => ({ ...prev, open: false, data: {} }));
    setJourney({ options: [] });
    replace([]);
  };
  const onSubmit = (data) => {
    // Verify sum of proportions
    if (data.items.reduce((n, i) => n + parseFloat(i.proportion), 0) !== 1) {
      setError({
        message: t('pages.scenarios.configure.patients.form.validation.sum'),
      });
      return;
    }

    // TODO: back-end to provide endpoint using unit IDs
    const options_override = journey.options.map((option) => {
      const item = data.items.find((i) => i.end_unit === option.id);
      return {
        ...option,
        proportion: parseFloat(item.proportion),
      };
    });
    const journey_override = cloneDeep(patientCategory.journey);
    remove(journey_override, (i) => i.start_unit === unit.name);
    journey_override.push({ ...journey, options: options_override });
    mutate({
      scenarioId,
      patientCategoryId: patientCategory.id,
      data: {
        kind: 'set',
        journey_override,
      },
    });
  };

  useEffect(() => {
    // TODO: show mutation error
    if (!isMutationPending && isMutationSuccess) {
      onClose();
    }
  }, [isMutationPending, isMutationSuccess]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: 'primary.main' }}>
        {journey?.start_unit}
      </DialogTitle>
      <DialogContent>
        {activePatientCategory && journey && journey.options.length ? (
          <Box
            id="journey-form"
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack spacing={2}>
              {!!error && <Alert severity="error">{error.message}</Alert>}
              <Typography>
                {t('pages.scenarios.configure.patients.form.options')}
              </Typography>
              {fields.map((field, index) => (
                <Stack key={field.id} spacing={1}>
                  <Typography variant="overline">{field.end_unit}</Typography>
                  <TextField
                    label={t(
                      'pages.scenarios.configure.patients.form.proportion',
                    )}
                    tooltip={t(
                      'pages.scenarios.configure.patients.form.proportionTooltip',
                    )}
                    name={`items.${index}.proportion`}
                    control={control}
                    rules={{
                      required: t('common.validation.required'),
                      min: {
                        value: 0.01,
                        message: t('common.validation.min', { value: 0.01 }),
                      },
                    }}
                    type="number"
                  >
                    {field.proportion}
                  </TextField>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : !activePatientCategory ? (
          <Typography>
            {t('pages.scenarios.configure.patients.form.missing')}
          </Typography>
        ) : (
          <Typography>
            {t('pages.scenarios.configure.patients.form.empty')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isMutationPending} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        {activePatientCategory && journey && journey.options.length && (
          <Button
            variant="contained"
            type="submit"
            form="journey-form"
            startIcon={<FontAwesomeIcon icon="floppy-disk" />}
            loading={isMutationPending}
            loadingPosition="start"
          >
            {t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default JourneyDialog;
